import { motion } from "framer-motion";
import { FINANCIAL_ABSTRACT, BOOKING_URL } from "@/lib/constants";
import { ArrowUpRight } from "lucide-react";

export default function Contingency() {
  return (
    <section
      id="contingency"
      data-testid="contingency-section"
      className="relative section-dark overflow-hidden py-24 sm:py-32"
    >
      <motion.div
        aria-hidden
        className="absolute inset-0 opacity-30"
        initial={{ scale: 1.1 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 2 }}
      >
        <img
          src={FINANCIAL_ABSTRACT}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#040b16] via-[#040b16]/70 to-[#040b16]" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#F5B800]" />
              <span className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#F5B800]">
                05 · Contingency Model
              </span>
            </div>
            <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              You only pay
              <br />
              <span className="italic text-shimmer">when we win.</span>
            </h2>
            <p className="mt-8 text-lg text-white/70 max-w-md leading-relaxed">
              Our incentives are perfectly aligned with yours. If we don&apos;t
              recover a single dollar, you owe us nothing.
            </p>

            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contingency-cta"
              className="btn-magnetic mt-10 inline-flex items-center gap-3 bg-[#F5B800] hover:bg-[#DDA600] text-[#0A1936] font-semibold text-base px-8 py-5 rounded-sm shadow-[0_10px_40px_-10px_rgba(245,184,0,0.6)] transition-colors"
            >
              Start with a Free Assessment
              <ArrowUpRight size={20} />
            </a>
          </motion.div>

          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            {[
              {
                metric: "$0",
                label: "Upfront cost to engage",
                testId: "contingency-metric-upfront",
              },
              {
                metric: "20%",
                label: "Only on successful recovery",
                testId: "contingency-metric-fee",
              },
              {
                metric: "$0",
                label: "Owed if no recovery",
                testId: "contingency-metric-norisk",
              },
              {
                metric: "100%",
                label: "Our risk. Not yours.",
                testId: "contingency-metric-ourrisk",
              },
            ].map((m, i) => (
              <motion.div
                key={m.label}
                data-testid={m.testId}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="relative rounded-md border border-white/10 bg-white/5 backdrop-blur-md p-8 hover:border-[#F5B800] transition-colors"
              >
                <div className="font-serif text-white text-5xl sm:text-6xl leading-none tracking-tight">
                  {m.metric}
                </div>
                <div className="mt-4 h-px w-8 bg-[#F5B800]" />
                <div className="mt-3 text-sm text-white/70 leading-relaxed">
                  {m.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
